<template>
  <v-layout wrap class="date-field">
    <v-flex xs12>
      <v-layout wrap row>
        <v-flex xs11 md11 v-if="embedded && !displayTime">
          <v-text-field box clearable slot="activator" v-model="dateInputText" :label="name" @focus="dateFocus"
            @blur="dateBlurred()" append-icon="event" @click:append="openDatePicker($event)"
            :placeholder="field.input_format || field.format || 'DD-MM-YYYY'">
          </v-text-field>

          <div class="date-picker-content v-card embedded"
            :style="{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }" ref="datepicker"
            v-if="datePickerVisible">
            <div class="action-header"><font-awesome-icon size="lg" :icon="['fas', 'times']"
                @click="closeDatePicker()" /></div>
            <v-date-picker v-model="date" no-title @input="menu = false" v-on:blur="dateBlurred()" show-current
              v-if="datePickerVisible" :close-on-content-click="true"></v-date-picker>
          </div>
        </v-flex>

        <v-flex xs11 md6 v-else>
          <v-text-field box slot="activator" v-model="dateInputText" :label="name" ref="dateInput" @focus="dateFocus"
            @blur="dateBlurred()" append-icon="event" @click:append="openDatePicker()"
            :placeholder="field.input_format || field.format || 'DD-MM-YYYY'">
          </v-text-field>
          <div class="date-picker-container" v-if="datePickerVisible">
            <div class="date-picker-content v-card">
              <div class="action-header"><font-awesome-icon size="lg" :icon="['fas', 'times']"
                  @click="closeDatePicker()" />
              </div>
              <v-date-picker class="date-picker" v-model="date" no-title @input="closeDatePicker()" color="primary"
                clearable v-on:blur="dateBlurred()" show-current>
              </v-date-picker>
            </div>
          </div>
        </v-flex>

        <v-flex xs11 md5 v-if="displayTime">
          <v-text-field box slot="activator" v-model="time" :disabled="readonly" label="Time" append-icon="schedule"
            v-on:blur="timeBlurred()" step="1" @click:append="openTimePicker($event)" placeholder="HH:MM:SS">
          </v-text-field>
          <div class="time-picker-container" v-if="timePickerVisible">
            <div class="time-picker-content primary v-card">
              <div class="action-header">
                <font-awesome-icon size="lg" :icon="['fas', 'times']" @click="closeTimePicker" />
              </div>
              <v-time-picker class="date-picker" :scrollable="true" v-model="time" :use-seconds="true"
                @change="closeTimePicker" color="primary" v-on:blur="timeBlurred()" :format="timeFormat"
                show-current></v-time-picker>
            </div>
          </div>
        </v-flex>
        <v-flex xs1 md1 v-if="dateInputText && !readonly">
          <div class="clear-date-icon">
            <v-icon @click="clearDate">clear</v-icon>
          </div>
        </v-flex>
        <v-flex xs1 md1 v-if="hasGuidance" class="guidance" @click="guidanceVisible = true">
          <font-awesome-icon size="lg" :icon="['fas', 'info-circle']" />
        </v-flex>
      </v-layout>
      <v-dialog v-model="guidanceVisible" v-if="guidanceVisible" max-width="500">
        <v-card class="guidance-card">
          <v-card-title><font-awesome-icon :icon="['fas', 'info-circle']" /> Guidance for {{ fieldName }}</v-card-title>
          <v-card-text>{{ field.guidance }}</v-card-text>
        </v-card>
      </v-dialog>

    </v-flex>
  </v-layout>
</template>

<script>
import Helpers from "@/helpers.js"
import moment from 'moment'

export default {
  name: "date-field",
  props: {
    field: {
      type: Object
    },
    data: {
      type: String
    },
    embedded: {
      type: Boolean,
      default: false
    },
    row: {
      type: Number
    },
    popupCounter: {
      type: Number,
      required: false
    }
  },
  data: () => ({
    menu: false,
    date: undefined,
    dateInputText: "",
    time: undefined,
    cleared: false,
    datePickerVisible: false,
    timePickerVisible: false,
    guidanceVisible: false,
    popupPosition: { top: 0, left: 0 }
  }),
  mounted() {
    if (this.data !== undefined && this.data !== null) {
      this.date = this.data
    } else {
      if (this.field.default_value !== undefined) {
        this.date = this.field.default_value
      }
    }
    this.dateInputText = this.formattedDate
    this.setTimeBasedOnDate()
  },
  computed: {
    fieldName() {
      return Helpers.titleize(this.field.name)
    },
    hasGuidance() {
      return this.field.guidance && this.field.guidance.length > 0
    },
    timeFormat() {
      return this.field.time_format === "ampm" ? "ampm" : "24hr"
    },
    times() {
      let results = []
      const hours = Array.from(Array(24), (_, h) => h)
      hours.forEach(
        hour => (results = results.concat(
          ["00", "15", "30", "45"].map(min =>
            `${hour}.${min}`.padStart(5, "0")
          )
        ))
      )
      return results
    },
    displayTime() {
      return !(this.field.display && this.field.display === "date_only")
    },
    name() {
      return this.field.display_name
        ? this.field.display_name
        : Helpers.capitalizeRemoveUnderscores(this.field.name)
    },
    format() {
      let form = "DD-MM-YYYY"
      if (this.field.format) {
        form = this.field.format
      }
      return form
    },
    formattedDate: {
      get() {
        if (!this.date) return null
        const result = Helpers.formatDate(this.date, this.format)
        return result
      },
      set(value) {
        if (value === null) {
          this.date = null
          this.time = null
          this.cleared = true
        } else {
          this.cleared = false
        }
      }
    },
    dateTime() {
      if (this.date && this.date.toString().includes("Z")) {
        return this.date
      } else if (this.date) {
        const enteredDate = new Date(this.date)
        if (this.time && this.time.includes(":")) {
          const splits = this.time.split(":")
          let hrs = 0
          let mins = 0
          let secs = 0
          if (splits.length === 3) {
            secs = parseInt(splits[2])
          }
          hrs = parseInt(splits[0])
          mins = parseInt(splits[1])
          enteredDate.setHours(hrs, mins, secs)
        }
        return enteredDate.toISOString()
      }
      return undefined
    },
    readonly() {
      let readonly = false
      if (this.field && this.field.readonly) {
        readonly = this.field.readonly
      }
      return readonly
    }
  },
  methods: {
    openDatePicker(event) {
      const currentlyVisible = this.datePickerVisible
      this.$emit("popupVisible")

      this.$nextTick(() => {
        if (event !== undefined && event !== null) {
          const target = event.target.closest(".v-input")
          const parent = target.closest(".relative")
          if (parent !== undefined && parent !== null) {
            const targetRect = target.getBoundingClientRect()
            const parentRect = parent.getBoundingClientRect()
            // Calculate position relative to the parent
            this.popupPosition = {
              top: targetRect.bottom - parentRect.top,
              left: targetRect.left - parentRect.left,
            }
          }
        }
        this.datePickerVisible = !currentlyVisible
      })
    },
    closeDatePicker() {
      this.datePickerVisible = false
    },
    openTimePicker(event) {
      const currentlyVisible = this.timePickerVisible
      this.$emit("popupVisible")
      this.$nextTick(() => {
        if (event !== undefined && event !== null) {
          const target = event.target.closest(".v-input")
          const parent = target.closest(".relative")
          if (parent !== undefined && parent !== null) {
            const targetRect = target.getBoundingClientRect()
            const parentRect = parent.getBoundingClientRect()
            // Calculate position relative to the parent
            this.popupPosition = {
              top: targetRect.bottom - parentRect.top,
              left: targetRect.left - parentRect.left,
            }
          }
        }
        this.timePickerVisible = !currentlyVisible
      })
    },
    closeTimePicker(external = false) {
      this.timePickerVisible = false
    },
    setTimeBasedOnDate() {
      if (this.date) {
        const date = new Date(this.date)
        const h = date
          .getHours()
          .toString()
          .padStart(2, "0")
        const m = date
          .getMinutes()
          .toString()
          .padStart(2, "0")
        const s = date
          .getSeconds()
          .toString()
          .padStart(2, "0")
        this.time = `${h}:${m}:${s}`
      }
    },
    dateFocus(e) {
      if (this.field.input_format && this.field.input_format !== "" && this.date !== null) {
        this.dateInputText = moment(this.date).format(this.field.input_format)
      }
      this.$nextTick(() => {
        e.target.select()
      })
    },
    dateBlurred() {
      if (this.dateInputText !== "") {
        const dateFormat = this.field.input_format || this.field.format || "DD-MM-YYYY"
        const currDate = moment(this.dateInputText, dateFormat, true)
        if (currDate.format() !== "Invalid date") {
          if (this.time !== "" && this.time !== undefined && this.time !== null) {
            const [hrs, minutes, seconds] = this.time.split(":")
            currDate.set({
              hour: hrs || 0,
              minute: minutes || 0,
              seconds: seconds || 0
            })
          }
          this.date = currDate.toISOString()
          this.dateInputText = this.formattedDate
        }
      }
    },
    timeBlurred() {
      const currDate = moment(this.date)
      if (this.time !== "" && this.time !== undefined && this.time !== null) {
        const [hrs, minutes, seconds] = this.time.split(":")
        currDate.set({
          hour: hrs || 0,
          minute: minutes || 0,
          seconds: seconds || 0
        })
      } else {
        currDate.set({
          hour: 0,
          minute: 0,
          seconds: 0
        })
      }
      this.date = currDate.toISOString()
      this.dateInputText = this.formattedDate
    },
    clearDate() {
      this.date = null
      this.time = null
      this.dateInputText = ""
      this.cleared = true
      this.closeDatePicker()
      this.closeTimePicker()
    },
  },
  watch: {
    popupCounter() {
      this.closeDatePicker()
      this.closeTimePicker()
    },
    data() {
      this.date = this.data
      this.setTimeBasedOnDate()
    },
    date() {
      this.dateInputText = this.formattedDate
      this.$emit("update-item", {
        field: this.field.name,
        data: this.dateTime,
        row: this.row
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.clear-date-icon {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 55px;

  i {
    color: rgba(0,0,0,0.54);
  }
}


.date-picker-container,
.time-picker-container {
  position: relative;
}

.date-picker-content,
.time-picker-content {
  position: absolute;
  top: -30px;
  right: 0px;
  z-index: 3000;
  background: white;

  &.primary {
    color: white;
  }

  .action-header {
    height: 20px;
    display: flex;
    justify-content: flex-end;
    margin-right: 10px;
    margin-top: 10px;
  }

  &.embedded {
    right: initial;

    ::v-deep {

      .v-picker {
        padding-bottom: 10px;
      }

    }
  }

  ::v-deep {
    .v-picker {
      padding-bottom: 10px;
      border-top: 0;
      box-shadow: initial;
    }

    .v-time-picker-title {
      justify-content: center;

      .v-time-picker-title__time .v-picker__title__btn,
      .v-time-picker-title__time span {
        font-size: 40px;
        height: 40px;
      }
    }
  }


  .date-picker {
    box-shadow: initial;
  }
}

.guidance {
  margin: 20px;
  max-width: 20px;
  cursor: pointer;
}

.guidance-card {
  margin: 10px;
  font-size: 0.9em;

  .v-card__title {
    font-size: 1.3em;
    padding: 10px;
    display: flex;
    align-items: center;
    margin-bottom: 0;

    svg {
      margin-right: 10px;
    }
  }

  .v-card_text {
    padding: 10px;
  }
}

.my-custom-table {
  position: initial;
}
</style>

<style>
.embedded .v-picker table tbody td,
.embedded .v-picker table tbody td th {
  height: 10px;
}
</style>